import { useEffect, useState } from "react";
import { BooleanInput, Identifier, ListProps, minValue, NumberField, NumberInput, ReferenceField, ReferenceInput, required, SelectInput, TextField, TextInput, useDataProvider } from "react-admin";
import { DialogForm, DialogGrid, DialogList, GeometryField, Row, TranslateField } from "../common/components";
import { tracking } from "../common/components/utils/choice";
import { filter } from "../common/components/utils/fn";
import { getLot } from "../services/lot/actions";
import { createProductionUnits } from "../services/productionUnit/actions";
import { TaskTrackingList } from "./Task";

export interface ProductionUnitProps extends ListProps {
    lotId?: Identifier;
    key?: any;
    simple?: Boolean;
    hasMap: boolean;
};

const CreateDialog = ({ lotId, hasMap, ...props }) => {
    const [displayAuto, setDisplayAuto] = useState("none");
    const dataProvider = useDataProvider();
    return <DialogForm {...props} key={`key${lotId}`} initialValues={{ lotId, numberProductionUnits: 1, automaticIncrementName: true, tracking: "waiting" }} saveAction={async (form) => {
        const state = form.getState();
        await createProductionUnits(dataProvider, state.values);
    }}>
        <NumberInput source="numberProductionUnits" validate={[required(), minValue(1)]} onChange={({ target: { value } }) => { setDisplayAuto(value > 1 ? "block" : "none"); }} />
        <Row>
            <TextInput source="name" validate={required()} />
            <BooleanInput style={{ display: displayAuto, flex: 0.4 }} source="automaticIncrementName" />
        </Row>
        <NumberInput source="quantity" validate={[required(), minValue(1)]} />
        <TextInput source="comment" multiline />
        {lotId === undefined && <ReferenceInput source="lotId" reference="lot" validate={required()}>
            <SelectInput />
        </ReferenceInput>}
        {hasMap && <TextInput source="geometry" multiline />}
    </DialogForm>
};

const EditDialog = ({ lotId, lotEditable, hasMap, ...props }) => <DialogForm {...props} hasDelete={lotEditable} initialValues={{ lotId }}>
    <TextInput disabled={!lotEditable} source="name" validate={required()} />
    <NumberInput disabled={!lotEditable} source="quantity" validate={[required(), minValue(1)]} />
    <TextInput disabled={!lotEditable} source="comment" multiline />
    <SelectInput disabled source="tracking" choices={tracking} />
    <ReferenceInput disabled={!lotEditable} source="lotId" reference="lot" validate={required()} filter={{ project: filter("lot.id", lotId), tracking: "waiting" }}>
        <SelectInput />
    </ReferenceInput>
    {hasMap && <TextInput source="geometry" multiline />}
</DialogForm>;


export const ProductionUnitList = (propsReceive: ProductionUnitProps) => {
    const dataProvider = useDataProvider();
    const [lotEditable, setLotEditable] = useState(false);
    const [productionUnitEditable, setProductionUnitEditable] = useState(false);
    const { lotId, hasMap, simple, ...propsOthers } = propsReceive;
    const hasInfos = simple !== true;
    useEffect(() => {
        if (lotId) {
            getLot(dataProvider, lotId).then((data) => {
                setLotEditable(data.tracking === "waiting");
            });
        }
    }, [dataProvider, lotId]);
    useEffect(() => {
        setProductionUnitEditable(lotEditable || hasMap);
    }, [lotEditable, hasMap]);
    const title = lotId ? " " : propsOthers.title;
    const filterDefaultValues = lotId ? { ...propsOthers.filterDefaultValues, lotId } : propsOthers.filterDefaultValues;
    const props = {
        ...propsOthers,
        resource: "production_unit",
        title,
        filterDefaultValues,
        id: `key${lotId}`
    };
    return <DialogList {...props} importer={() =>
        lotEditable ? {
            transformRows: (csvRows: any[]) => {
                const rows = [];
                for (var row of csvRows) {
                    rows.push({
                        lotId,
                        name: row["name"],
                        quantity: row["quantity"],
                        comment: row["comment"],
                        geometry: row["geometry"]
                    })
                }
                return rows;
            }
        } : false
    } create={lotEditable} bulkActionButtons={lotEditable} dialogCreate={<CreateDialog lotId={lotId} hasMap={hasMap} />} dialog={<EditDialog lotId={lotId} lotEditable={lotEditable} hasMap={hasMap} />}>
        <DialogGrid key={lotId} rowClick={productionUnitEditable ? "edit" : "expand"} isEdit={() => productionUnitEditable} expand={lotEditable ? undefined : ({ id }) => <TaskTrackingList productionUnitId={id} />}>
            <TextField source="name" />
            {hasInfos && <TextField source="stageName" />}
            <NumberField source="quantity" />
            {hasInfos && <NumberField source="quantityReal" />}
            <TextField source="comment" />
            <TranslateField source="tracking" />
            {lotId === undefined && <ReferenceField source="lotId" reference="lot">
                <TextField source="name" />
            </ReferenceField>}
            
            {hasMap && <GeometryField source="geometry"  />}
        </DialogGrid>
    </DialogList>
};